import { motion } from 'framer-motion';
import { ArrowUpRight, CalendarDays, Clock } from 'lucide-react';
import { EVENTS, BLOG } from '@/lib/site';

export default function Events() {
  return (
    <section
      id="events"
      data-testid="events-section"
      className="relative py-24 md:py-32"
    >
      <div className="container-x">
        {/* Events timeline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <div className="lg:col-span-4">
            <p className="eyebrow">Upcoming · Campaigns</p>
            <h2 className="h-display mt-4 text-4xl md:text-5xl font-bold leading-[1.05] text-maroon-700 dark:text-foreground">
              Where you can
              <br />
              <span className="italic font-serif saffron-text">find us next.</span>
            </h2>
            <p className="mt-5 text-foreground/65 max-w-md">
              Field camps, drives, and city pop-ups across India. Walk in, volunteer for an hour
              or simply visit and learn how the work happens.
            </p>
          </div>

          <div className="lg:col-span-8">
            <ol className="relative border-l-2 border-saffron-500/30 pl-6 md:pl-10 space-y-7">
              {EVENTS.map((e, i) => (
                <motion.li
                  key={e.title}
                  data-testid={`event-${i}`}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.06 * i }}
                  className="relative"
                >
                  <span className="absolute -left-[37px] md:-left-[49px] top-2 grid h-5 w-5 place-items-center rounded-full bg-saffron-500 ring-4 ring-saffron-500/20">
                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                  </span>
                  <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-3 md:gap-6 items-start group rounded-2xl border border-border/70 bg-card p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all">
                    <div className="text-sm">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-saffron-50 dark:bg-saffron-500/10 px-2.5 py-1 text-xs font-semibold text-saffron-700 dark:text-saffron-300">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {e.date}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] uppercase tracking-wider font-semibold text-maroon-700 dark:text-saffron-200">
                          {e.tag}
                        </span>
                      </div>
                      <h3 className="mt-1 font-display text-xl md:text-2xl font-semibold text-foreground group-hover:text-saffron-600 transition">
                        {e.title}
                      </h3>
                      <p className="mt-1 text-sm text-foreground/65">{e.desc}</p>
                    </div>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>

        {/* Blog cards */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="eyebrow">From our journal</p>
            <h3 className="h-display mt-3 text-3xl md:text-4xl font-bold text-maroon-700 dark:text-foreground">
              Notes on seva, written by people who do it.
            </h3>
          </div>
          <a
            href="#"
            className="inline-flex items-center gap-1 text-sm font-semibold text-saffron-600 dark:text-saffron-300 hover:gap-2 transition-all"
          >
            All articles <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {BLOG.map((b, i) => (
            <motion.article
              key={b.title}
              data-testid={`blog-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.06 * i }}
              className="group rounded-3xl overflow-hidden bg-card border border-border/70 hover:shadow-xl transition-all"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={b.image}
                  alt={b.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 text-xs text-foreground/55">
                  <span>{b.date}</span>
                  <span className="h-1 w-1 rounded-full bg-saffron-500" />
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {b.readTime}
                  </span>
                </div>
                <h4 className="mt-2 font-display text-xl font-semibold text-foreground group-hover:text-saffron-600 transition">
                  {b.title}
                </h4>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground/65">{b.excerpt}</p>
                <a
                  href="#"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-saffron-600 dark:text-saffron-300 hover:gap-2 transition-all"
                >
                  Read article <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
